import { useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  COUNTRIES,
  REGIONS,
  countriesInRegion,
  type Country,
  type CountryRegion,
} from "./countries";

/**
 * Shared country picker used by:
 *   - Nationality field (RegisterScreen + CompanionsScreen)  — variant="full"
 *   - Phone-country selector inside PhoneInput                — variant="phone"
 *
 * Panel layout matches industry convention (Airbnb / Cloudbeds / Amadeus):
 *   - Search input at top: hits any country by name substring across ALL
 *     regions when there's a query.
 *   - Region tab strip: default "Europe & nearby", plus Middle East / Africa
 *     / Asia / Americas / Oceania.
 *   - Scrollable list underneath, alphabetical.
 *
 * When a search query is present the region tabs are ignored — the query is
 * matched against the full country list so the guest never has to guess which
 * continent bucket their country landed in.
 */
export function CountryPicker({
  value,
  onChange,
  placeholder = "Select country",
  variant = "full",
  disabled = false,
  triggerClassName,
  inputId,
}: {
  value: string | null | undefined;
  onChange: (iso2: string) => void;
  placeholder?: string;
  /** "full" shows flag + country name; "phone" shows flag + dial code (compact). */
  variant?: "full" | "phone";
  disabled?: boolean;
  triggerClassName?: string;
  inputId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<CountryRegion>("europe_and_nearby");
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);

  const selected = COUNTRIES.find((c) => c.iso2 === value) ?? null;

  const list = useMemo<Country[]>(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      return COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase() === q ||
        c.dialCode.replace(/\D/g, "").includes(q.replace(/\D/g, ""))
      );
    }
    return countriesInRegion(region);
  }, [query, region]);

  const triggerLabel = selected
    ? (variant === "phone"
        ? `${selected.flag} ${selected.dialCode}`
        : `${selected.flag} ${selected.name}`)
    : placeholder;

  function pick(iso2: string) {
    onChange(iso2);
    setOpen(false);
    setQuery("");
  }

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) {
          // Focus the search field on open — the guest can start typing the
          // country name immediately without a second tap.
          setTimeout(() => searchRef.current?.focus(), 30);
        } else {
          setQuery("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          id={inputId}
          className={cn(
            "justify-between font-normal",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // 88vw caps ensure the picker never overflows a narrow phone viewport.
        className="w-[min(20rem,88vw)] p-0"
      >
        <div className="p-2 space-y-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country…"
              className="pl-8 h-9"
              autoComplete="off"
            />
          </div>
          {/* Region tabs hidden while a search query is active — the query
              already spans all regions, showing tabs would be confusing. */}
          {!query && (
            <div className="flex gap-1 flex-wrap">
              {REGIONS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRegion(r.id)}
                  className={cn(
                    "px-2 py-1 rounded-full text-[11px] font-medium leading-none border",
                    region === r.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-muted text-muted-foreground border-transparent hover:bg-accent",
                  )}
                >
                  {r.short}
                </button>
              ))}
            </div>
          )}
        </div>
        <ul className="max-h-72 overflow-y-auto py-1">
          {list.length === 0 && (
            <li className="px-3 py-4 text-sm text-muted-foreground text-center">
              No matches — try a different spelling.
            </li>
          )}
          {list.map((c) => {
            const active = c.iso2 === value;
            return (
              <li key={c.iso2}>
                <button
                  type="button"
                  onClick={() => pick(c.iso2)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent",
                    active && "bg-accent/60",
                  )}
                >
                  <span className="text-base leading-none w-5 shrink-0">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{c.dialCode}</span>
                  {active && <Check className="h-4 w-4 ml-1 text-primary" />}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
